import { Badge, CollapsedContent, Icon } from '@aqarmap/aqarmap-ui';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { SubscriptionFlagsEnum } from '~/constants/enums';
import { useFullRoutes } from '~/hooks';
import { formatNumber } from '~/lib';
import {
  getPackageTierLocations,
  PackagesGridPlanType,
} from '~/pages/Packages/utils';
import { openModal } from '~/redux/features/uiState/uiStateSlice';
import { useAppDispatch } from '~/redux/hooks';
import { PackageTierType } from '~/types/packagesType';
import Tooltip from '~/components/Tooltip';
import ServicesList from '~/pages/Packages/ServicesList';

export default function PlanCard({
  plan,
  mobileScreen,
  onTiersClick,
  allowActivationFees = false,
}: {
  plan: PackagesGridPlanType;
  onTiersClick: (tiers: PackageTierType[], name: string | null) => void;
  mobileScreen: boolean;
  allowActivationFees?: boolean;
}) {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const navigate = useNavigate();
  const { PACKAGES_URL, SUBSCRIPTION_URL } = useFullRoutes();
  const dispatch = useAppDispatch();
  const tiers = plan?.tiers as PackageTierType[];
  const hasServices = plan.services.length > 0;
  const planCredit = plan.credit ?? 0;
  const hasCredit = Boolean(planCredit && Number(planCredit) > 0);
  const priceBeforeDiscount = plan.price_before_discount ?? 0;
  const discountPercentage = plan.discount_percentage;

  const amountOfDiscount = discountPercentage
    ? Number(priceBeforeDiscount) - Number(plan.price)
    : 0;
  const hasDiscount = amountOfDiscount > 0;
  const flagsLabel: Record<SubscriptionFlagsEnum, string> = {
    [SubscriptionFlagsEnum.BEST_SELLER]: t('bestSeller'),
    [SubscriptionFlagsEnum.BEST_FOR_BRANDING]: t('bestForBranding'),
    [SubscriptionFlagsEnum.BIGGEST_DISCOUNT]: hasDiscount
      ? `${t('saveIt')} ${discountPercentage}%`
      : t('biggestDiscount'),
    [SubscriptionFlagsEnum.RECOMMENDED]: t('recommended'),
    [SubscriptionFlagsEnum.SPECIAL_OFFER]: t('specialOffer'),
  };
  const tagLabel = plan.flag
    ? flagsLabel[plan.flag as SubscriptionFlagsEnum]
    : null;

  const locations = getPackageTierLocations(tiers);
  const showLocationsSection = !(
    plan.type === 'subscription' && tiers.length === 0
  );

  const isHidden = (value: any) => {
    return !value && mobileScreen;
  };
  return (
    <div
      className={`flex w-full relative h-full flex-col rounded-[12px] border bg-white ${
        tagLabel ? 'border-orange' : 'border-gray'
      }`}
    >
      {tagLabel ? (
        <Badge
          text={tagLabel}
          className="rounded-full bg-orange  absolute top-[-16px] end-3x py-0.5x px-2x text-caption-2"
        />
      ) : null}

      <div className="flex h-full flex-col px-3x py-3x">
        <div className="flex flex-col rounded-t-[12px]">
          <div className="flex justify-between w-full items-center mb-1.5x ">
            <p
              className={[
                'text-title-4 text-black truncate max-w-full ',
                'min-w-0',
              ].join(isHidden(plan.title) ? 'hidden' : 'min-h-[24px]')}
            >
              {plan.title}
            </p>
            {plan.is_rental && (
              <Badge
                text={t('rentOnly')}
                className="rounded-[6px] bg-blue_light_2 text-blue px-1.5x text-caption-2"
              />
            )}
          </div>

          <p
            className={[
              'text-body-2 text-gray__dark_1 mb-3x truncate',
              isHidden(plan.description) ? 'hidden' : ' min-h-[24px]',
            ].join(' ')}
          >
            {plan.description}
          </p>

          <div className="flex items-center gap-x mb-2x">
            {hasDiscount ? (
              <span className="line-through text-gray__dark_1 text-title-4 whitespace-nowrap">
                <Trans
                  i18nKey={'priceValue'}
                  t={t}
                  values={{
                    value: formatNumber(Number(plan.price_before_discount)),
                  }}
                  components={{
                    tag: <span>placeholder</span>,
                  }}
                />
              </span>
            ) : null}
            <p className="text-title-1 text-black whitespace-nowrap">
              <Trans
                i18nKey="priceValuePerMonth"
                t={t}
                values={{
                  value: formatNumber(Number(plan.price)),
                }}
                components={{
                  tag: <span className="text-body-1">placeholder</span>,
                }}
              />
            </p>
          </div>

          <div
            className={[
              ' flex items-center gap-0.5x mb-3x',
              isHidden(plan.credit) ? 'hidden' : 'min-h-[24px]',
            ].join(' ')}
          >
            {hasCredit ? (
              <>
                <p className="text-body-1 text-gray__dark_2">
                  <Trans
                    i18nKey="pointsIncludedValue"
                    t={t}
                    value={formatNumber(planCredit?.toString())}
                    values={{
                      value: formatNumber(planCredit?.toString()),
                    }}
                    components={{
                      tag: <span className=" text-black">placeholder</span>,
                    }}
                  />
                </p>

                <Tooltip
                  id={`pointsTooltip${plan.key}`}
                  text={t('pointsTooltip')}
                  className="max-w-[272px]"
                >
                  <Icon name="info" className="text-gray__dark_1" size="16px" />
                </Tooltip>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex gap-1.5x flex-col mb-3x">
          <button
            type="button"
            onClick={() =>
              navigate(
                plan.type === 'subscription'
                  ? SUBSCRIPTION_URL.CHECKOUT(plan.checkoutId)
                  : PACKAGES_URL.CHECKOUT(plan.checkoutId),
              )
            }
            className="btn-primary btn-medium rounded-[26px] element-center "
          >
            {t('subscribeNow')}
          </button>
          {allowActivationFees ? (
            <div
              className={[
                'flex gap-x justify-center',
                isHidden(plan.activation_fee) ? 'hidden' : 'min-h-[24px]',
              ].join(' ')}
            >
              {plan.activation_fee ? (
                <>
                  <p className="text-caption-2 text-gray__dark_1">
                    <Trans
                      i18nKey="activationFees"
                      t={t}
                      values={{
                        value: formatNumber(plan.activation_fee.toString()),
                      }}
                      components={{
                        tag: (
                          <span className="text-caption-1 text-gray__dark_2" />
                        ),
                      }}
                    />
                  </p>
                  <div className="flex gap-0.5x text-caption-1">
                    <Trans
                      i18nKey="priceValue"
                      t={t}
                      values={{
                        value: formatNumber(plan.activation_fee.toString()),
                      }}
                      components={{
                        tag: <span />,
                      }}
                    />
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
        {!showLocationsSection ? null : tiers === null ? (
          <div
            className={[isHidden(tiers) ? 'hidden' : 'min-h-[58px]'].join(' ')}
          />
        ) : (
          <div className="flex items-center mb-3x">
            <div className="flex w-full items-center justify-between gap-1x  py-2x  border-y border-gray_light_1">
              <p className="text-caption-1 text-gray__dark_2">
                {t('availableAreas')}
              </p>
              {tiers && tiers.length > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    onTiersClick(tiers, plan.title);
                    dispatch(openModal('packageTiersModal'));
                  }}
                  className="flex items-center gap-0.5x text-blue"
                >
                  <p className="text-link-1">{`${locations?.length} ${t('areas')}`}</p>
                  <Icon
                    name="chevron-left"
                    size="16px"
                    className={locale === 'en' ? 'rotate-180' : ''}
                  />
                </button>
              ) : (
                <p className="text-link-1 text-gray__dark_2">{t('allAreas')}</p>
              )}
            </div>
          </div>
        )}

        {hasServices ? (
          mobileScreen ? (
            <CollapsedContent
              maxLines={3}
              itemHeight={24}
              itemsGap={8}
              text={{ less: t('lessServices'), more: t('moreServices') }}
              btnClassName=" more-services-btn"
            >
              <ServicesList plan={plan} />
            </CollapsedContent>
          ) : (
            <ServicesList plan={plan} />
          )
        ) : null}
      </div>
    </div>
  );
}
